import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import Google from "@mui/icons-material/Google";
import { motion } from "framer-motion";
import LottiePlayer from "../utilComp/LottiePlayer";
import animateData from "../../assets/lottieFiles/success.json";
import { useGetUserInfo, useUserLogin } from "../../services/query/mutation";
import { UserLoginType } from "../../types/types";
import useAuthProvider from "../../store/AuthProvider";
import generateConfig from "../../util/config";

const successCopy = {
  title: "You are all set!",
  body: "Thanks for signing in. Redirecting you to your previous destination now.",
};

function SignInExperience() {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [prevPath, setPrevPath] = useState<string>("/");
  const auth = useAuthProvider();
  const { replace } = useRouter();
  const containerRef = useRef(null);

  const { mutateAsync: createUser } = useUserLogin({
    onSuccess: (data) => {
      setIsLoggedIn(true);
      auth.login(data.result.token, {
        name: data.result.name,
        email: data.result.email,
        id: data.result._id,
        profile: data.result.picture,
      });
      replace(prevPath);
    },
  });
  const { mutateAsync, isLoading } = useGetUserInfo({
    onSuccess: (data) => {
      const formData = {
        name: data.name || "",
        email: data.email || "",
        picture: data.picture || "",
        sub: data.sub || "",
      } as UserLoginType;
      createUser(formData);
    },
  });

  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      mutateAsync(tokenResponse.access_token);
    },
  });

  useEffect(() => {
    const path = sessionStorage.getItem("prevPath");
    if (!path) return;
    if (auth.isAuthenticated) {
      replace(path);
      return;
    }
    setPrevPath(path);
  }, [auth.isAuthenticated, replace]);

  return (
    <Box ref={containerRef} sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 600 }}>
                Welcome back
              </Typography>
              <Typography variant="h1" sx={{ fontSize: { xs: "2.2rem", md: "3rem" }, fontWeight: 700 }}>
                Sign in to access personalized dashboards and conversations.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Authenticate with Google to sync your sessions, keep your chatbot history, and unlock beta features first.
              </Typography>
              <Alert severity="info" sx={{ borderRadius: "16px" }}>
                You will be redirected to the last page you visited once you are signed in.
              </Alert>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            {isLoggedIn ? (
              <Paper
                component={motion.div}
                elevation={0}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  borderRadius: "24px",
                  p: { xs: 4, md: 6 },
                  textAlign: "center",
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main}1a, ${theme.palette.secondary.main}33)`,
                  border: (theme) => `1px solid ${theme.palette.primary.light}`,
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
                  {successCopy.title}
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
                  {successCopy.body}
                </Typography>
                <Box sx={{ maxWidth: 320, mx: "auto" }}>
                  <LottiePlayer autoplay keepLastFrame speed={0.8} src={animateData} style={{ width: "100%" }} />
                </Box>
              </Paper>
            ) : (
              <Paper
                component={motion.div}
                elevation={0}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  borderRadius: "24px",
                  p: { xs: 4, md: 6 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  boxShadow: "0 30px 60px rgba(15, 23, 42, 0.12)",
                  border: "1px solid rgba(99, 102, 241, 0.15)",
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 600 }}>
                  Sign in with Google
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Secure OAuth sign-in. We never share your information without consent.
                </Typography>
                <Button
                  onClick={() => login()}
                  variant="outlined"
                  size="large"
                  startIcon={<Google fontSize="medium" />}
                  sx={{
                    borderRadius: "999px",
                    px: 3,
                    fontWeight: 600,
                  }}
                >
                  Continue with Google
                </Button>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
      <Backdrop
        sx={{
          color: "primary",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(2px)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

function MissingGoogleConfigNotice() {
  const envVar = "NEXT_PUBLIC_GOOGLE_CLIENT_ID";

  return (
    <Box sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            borderRadius: "24px",
            p: { xs: 4, md: 6 },
            boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
            border: "1px solid rgba(99, 102, 241, 0.2)",
          }}
        >
          <Stack spacing={3}>
            <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.6rem" }, fontWeight: 700 }}>
              Google sign-in is not configured yet
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {`Add your Google OAuth client ID to a local \`.env.local\` file to enable sign-in. The SDK requires the ${envVar} environment variable before initialising the login flow.`}
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: (theme) => theme.palette.grey[900],
                color: "#fff",
                borderRadius: 2,
                p: 2,
                fontFamily: "monospace",
                fontSize: "0.9rem",
              }}
            >
{`# .env.local
${envVar}=your-google-oauth-client-id`}
            </Box>
            <Typography variant="body2" color="text.secondary">
              After updating the env file, restart the dev server and reload this page.
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default function SignInComp() {
  const config = generateConfig();
  const hasGoogleClientId = Boolean(config.googleClientId);

  if (!hasGoogleClientId) {
    return <MissingGoogleConfigNotice />;
  }

  return <SignInExperience />;
}
